<script setup>
import fiveMixin from '@/composables/fiveMixin';
import useFunctions from '@/composables/useFunctions';

const { ...five } = fiveMixin();
const { ...useFun } = useFunctions();

const props = defineProps({
  prBirthData: { type: Object, default: null },
  prBirthJijanggan: { type: Object, default: () => ({}) },
  prLangMode: { type: String, default: '' },
  prBirthTitle: { type: String, default: '' },
  prType: { type: String, default: '' },
  prCurDaeun: { type: Object, default: () => ({}) },
  prTransdata: { type: Object, default: () => ({}) },
  isActive: { type: Boolean, default: false },
  selectedData: { type: Object, default: () => ({}) },
  activePage: { type: String },
  prPosition: { type: String },
  prTarget: { type: String }
});


function getYearSix(data, pos) {
  const posEng = getPosEng(pos);
  return data?.[posEng]?.six;
}

function getPosEng(pos) {
  return pos === '년' ? 'year'
    : pos === '월' ? 'month'
    : pos === '일' ? 'day'
    : pos === '시' ? 'hour'
    : '';
}

function getValueTxt(data, pos, val) {
  const posEng = getPosEng(pos);
  let txt = data?.[posEng]?.[val];
  if (val === 'sal') txt = txt ? txt[0] : '';
  return txt;
}

/** active 페이지 여러개 관리 */
// 확인 후 삭제
const isVisible = (...pages) => pages.includes(props.activePage);

/** 사주위치 형식 파싱 */
// '_'를 기준으로 분리 (앞이 무조건 천간/지지라는 보장 없기 때문) -> 객체 형태로
function parseSajuPosition(position) {
  if (!position) return { char: '', type: '' };
  const [first, second] = position.split('_');
  const charKeys = ['yy', 'mm', 'dd', 'ss'];
  const typeKeys = ['cc', 'gg'];
  let char = '', type = '';
  if (charKeys.includes(first) && typeKeys.includes(second)) {
    char = first; type = second;
  } else if (typeKeys.includes(first) && charKeys.includes(second)) {
    type = first; char = second;
  } else {
    console.warn('Invalid position:', position);
  }
  const charMap = { yy: '년', mm: '월', dd: '일', ss: '시' };
  const typeMap = { cc: 'sky', gg: 'earth' };
  return {
    char: charMap[char] ?? '',
    type: typeMap[type] ?? ''
  };
}

/** 색상 활성화 여부 */
// 사주 위치에 따라 + 일간은 무조건
function getOpacityClass(char, layerType) {
  const { char: activeChar, type: activeType } = parseSajuPosition(props.prPosition);
  if (char === activeChar && layerType === activeType) return 'active-opacity';
  if (layerType === 'sky' && char === '일' && isVisible('page2', 'page3', 'page4')) return 'active-opacity';
  if (props.prPosition) return 'inactive-opacity';
  return '';
}

/** 사주위치와 일치하는지 여부 */
function isPositionMatch(char, layerType) {
  const { char: posChar, type: posType } = parseSajuPosition(props.prPosition);
  return char === posChar && layerType === posType;
}

/** 풀이 대상 텍스트 클래스 */
// 사주 위치에 따른 풀이 대상 클래스 결정
function getPositionClass(char, layerType) {
  if (layerType === 'sky' && char === '일') return 'active-opacity';
  return isPositionMatch(char, layerType) ? 'active-opacity' : 'opacity-0';
}

/** active된 사주 위치에 나타낼 값 */
function getPositionText(char, layerType) {
  if (layerType === 'sky' && char === '일') {
    return `${getYearSix(props.prBirthData?.sky, char)} (나)`;
  }
  return isPositionMatch(char, layerType) ? props.prTarget : '';
}

/** 전역 인덱스 반환 */
function getGlobalIdx(type, title, prBirthTitle) {
  const posEng = getPosEng(title);
  const idxMap = {
    sky: { year: 0, month: 1, day: 2, hour: 3 },
    earth: { year: 4, month: 5, day: 6, hour: 7 }
  };
  const titleIndex = prBirthTitle.indexOf(title);
  if (titleIndex === -1) return null;
  return idxMap[type][posEng];
}

/** 간지별 클래스 */
function getClass(type, title, prBirthTitle) {
  if (!props.isActive || props.selectedData === null) return '';
  if (props.activePage !== 'page1') return '';
  const currentIdx = getGlobalIdx(type, title, prBirthTitle);
  const classes = [];
  if (currentIdx === props.selectedData) {
    if (currentIdx === 2) classes.push('ilgan');
  } else {
    classes.push('inactive-wongook');
  }
  return classes.join(' ');
}
</script>

<template>
  <div class="saju-summary__eight">
    <ul v-for="(char, index) in prBirthTitle" :key="index" class="birth-item-box birth txt-big">
      <li><span class="txt1">{{ char }}</span></li>

      <li class="summary-eight__li">
        <span class="txts" :class="getPositionClass(char, 'sky')">
          {{ getPositionText(char, 'sky') }}
        </span>
        <span
          class="txt-round xl"
          :class="[
            five.getColorFive(getValueTxt(prBirthData?.sky, char, 'value')),
            getClass('sky', char, prBirthTitle),
            prLangMode === 'CHNKOR' ? 'chnkor' : '',
            prLangMode,
            getOpacityClass(char, 'sky')
          ]"
        >
          <em v-if="prLangMode === 'CHNKOR'" class="chn">
            {{ five.getHanjaTxt(getValueTxt(prBirthData?.sky, char, 'value'), 'sky') }}
          </em>
          <em v-if="getValueTxt(prBirthData?.sky, char, 'value') === '-'" class="time-none">
            {{ getValueTxt(prBirthData?.sky, char, 'value') }}
          </em>
          <em v-else class="txt-main">
            {{ getValueTxt(prBirthData?.sky, char, 'value') }}
          </em>
        </span>
      </li>

      <li class="summary-eight__li">
        <span
          class="txt-round xl mb6"
          :class="[
            five.getColorFive(getValueTxt(prBirthData?.earth, char, 'value')),
            getClass('earth', char, prBirthTitle),
            prLangMode === 'CHNKOR' ? 'chnkor' : '',
            prLangMode,
            getOpacityClass(char, 'earth')
          ]"
        >
          <em v-if="prLangMode === 'CHNKOR'" class="chn">
            {{ five.getHanjaTxt(getValueTxt(prBirthData?.earth, char, 'value'), 'earth') }}
          </em>
          <em v-if="getValueTxt(prBirthData?.earth, char, 'value') === '-'" class="time-none">
            {{ getValueTxt(prBirthData?.earth, char, 'value') }}
          </em>
          <em v-else class="txt-main">
            {{ getValueTxt(prBirthData?.earth, char, 'value') }}
          </em>
        </span>
        <span class="txts txt-earth-02" :class="getPositionClass(char, 'earth')">
          {{ getPositionText(char, 'earth') }}
        </span>
      </li>
    </ul>

    <!-- 일간 테두리 -->
    <div
      v-if="activePage === 'page1'"
      class="active-border"
      :class="activePage"
    ></div>
  </div>

</template>

<style lang="scss" scoped>
.summary-eight__li{
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: visible;
}
</style>